import React, { useRef, useState } from "react";
import api from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, Link2, X } from "lucide-react";
import { toast } from "sonner";

/**
 * ImageUpload — reusable image field with drag-drop / file picker upload
 * OR direct URL paste. Emits the final URL (either /api/files/{id} or external).
 *
 * Props:
 *  - value:   string URL
 *  - onChange(url:string)
 *  - label?:  string
 *  - testid?: string  (base test id — inputs will use `${testid}-…`)
 *  - previewSize? "sm" | "md" | "cover" (4:5, cropped like the public cover)
 *  - hint?:   string  (shown under the label)
 *  - accept?: string  (default "image/*")
 */
export default function ImageUpload({ value, onChange, label, testid = "image-upload", previewSize = "md", accept = "image/*", hint }) {
  const inputRef = useRef(null);
  const [mode, setMode] = useState("upload"); // "upload" | "url"
  const [uploading, setUploading] = useState(false);

  const previewCls = previewSize === "sm" ? "w-16 h-16" : previewSize === "cover" ? "w-24 aspect-[4/5]" : "w-24 h-24";

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 1 * 1024 * 1024) { toast.error("Max 1MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2" data-testid={`${testid}-root`}>
      {label && <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>}
      {hint && <p className="text-xs text-muted-foreground" data-testid={`${testid}-hint`}>{hint}</p>}
      <div className="flex items-start gap-3">
        {/* Preview */}
        <div className={`${previewCls} shrink-0 rounded-xl border border-border bg-cream-100/50 flex items-center justify-center overflow-hidden`} data-testid={`${testid}-preview`}>
          {value ? (
            <img src={value} alt="" className={`w-full h-full ${previewSize === "cover" ? "object-cover" : "object-contain"}`} onError={(e) => { e.currentTarget.style.opacity = 0.3; }} />
          ) : (
            <span className="text-[10px] text-muted-foreground text-center px-1">No image</span>
          )}
        </div>

        <div className="flex-1 min-w-0 space-y-2">
          {/* Tab switcher */}
          <div className="inline-flex rounded-full border border-border p-0.5 text-xs">
            <button
              type="button"
              onClick={() => setMode("upload")}
              className={`px-3 py-1 rounded-full transition ${mode === "upload" ? "bg-terracotta-500 text-cream-50" : "text-muted-foreground hover:bg-secondary"}`}
              data-testid={`${testid}-mode-upload`}
            ><Upload className="w-3 h-3 inline mr-1" />Upload</button>
            <button
              type="button"
              onClick={() => setMode("url")}
              className={`px-3 py-1 rounded-full transition ${mode === "url" ? "bg-terracotta-500 text-cream-50" : "text-muted-foreground hover:bg-secondary"}`}
              data-testid={`${testid}-mode-url`}
            ><Link2 className="w-3 h-3 inline mr-1" />URL</button>
          </div>

          {mode === "upload" ? (
            <div>
              <input
                ref={inputRef}
                type="file"
                accept={accept}
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
                data-testid={`${testid}-file-input`}
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
                className="rounded-full h-9"
                data-testid={`${testid}-pick-btn`}
              >
                <Upload className="w-3.5 h-3.5 mr-1" />
                {uploading ? "Uploading…" : (value ? "Ganti Gambar" : "Pilih Gambar")}
              </Button>
              {value && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => onChange("")}
                  className="rounded-full h-9 text-destructive ml-1"
                  data-testid={`${testid}-clear-btn`}
                ><X className="w-3.5 h-3.5" /></Button>
              )}
              <div className="text-[10px] text-muted-foreground mt-1">JPG / PNG / WebP · max 1MB</div>
            </div>
          ) : (
            <div>
              <Input
                type="url"
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                placeholder="https://…/image.png"
                className="h-9 font-mono text-xs"
                data-testid={`${testid}-url-input`}
              />
              {value && (
                <Button type="button" variant="ghost" size="sm" onClick={() => onChange("")} className="rounded-full h-8 text-destructive mt-1" data-testid={`${testid}-clear-btn`}>
                  <X className="w-3.5 h-3.5 mr-1" /> Clear
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
